import {
  asCalendar,
  CalendarDefinitionSchema,
  GREGORIAN_CALENDAR_ID,
  isPluginCalendar,
  loadBundledCalendar,
  type Calendar,
} from '@dl/calendar';
import type { CalendarDefinitionLike, ConfigState } from '@dl/shared';

/**
 * Active-calendar resolution (#180, ADR-0004 §4/§5).
 *
 * The campaign's calendar CHOICE is a *setting* — the `canonical_calendar`
 * config key holds a calendar id (a `cal_<uuid>` for a pack/homebrew
 * definition, or a bundled id like `gregorian`). The calendar DEFINITION
 * lives in the #3 "definition" bucket: either a `pack.define_calendar`
 * folded into the registry overlay (`getHomebrewDefs().calendars`) or a
 * bundled-pack calendar.
 *
 * This module is the chokepoint that turns the folded config pointer plus
 * the folded calendar registry into the runtime `Calendar` the
 * canonical-day-count conversion engine consumes. It deliberately holds
 * no app-runtime dependency (no module-scope singletons, no `Model`), so
 * the resolution + fallback rules are unit-testable in isolation — see
 * `active-calendar.test.ts`. The wiring into the campaign-date cursor
 * lives in `app.ts`.
 *
 * Calendars are NOT lore entities (the prior assumption ADR-0004
 * retires): there is no entity scan here, only a config pointer resolved
 * against a definition registry.
 */

/**
 * The deterministic fallback calendar id used when `canonical_calendar`
 * is unset OR points at a calendar no registry/bundled definition can
 * resolve. Gregorian is the bundled earthlike default and preserves the
 * pre-settings behaviour (the client always defaulted to Gregorian until
 * a campaign declared its own calendar).
 *
 * NOTE (#183, D3): the bundled fallback resolves through `core.json`
 * today. When #183 removes `core.json` from the load path, the official
 * starter pack supplies the earthlike calendar instead; this constant
 * stays the deterministic default and the resolution rules below are
 * unchanged.
 */
export const FALLBACK_CALENDAR_ID = GREGORIAN_CALENDAR_ID;

/** A registry of calendar definitions folded from the op log. */
type CalendarRegistry = readonly CalendarDefinitionLike[];

/**
 * Does the registry or a bundled pack hold a definition for `calendarId`?
 * A pure existence check (no throw) used by {@link resolveActiveCalendarId}
 * to decide whether a config pointer is honourable.
 */
function isResolvable(calendarId: string, registry: CalendarRegistry): boolean {
  if (registry.some((c) => c.id === calendarId)) return true;
  try {
    loadBundledCalendar(calendarId);
    return true;
  } catch {
    return false;
  }
}

/**
 * Resolve the ACTIVE calendar id from the folded campaign config plus the
 * folded calendar registry.
 *
 * Rules:
 *   1. If `config.canonical_calendar` is set AND a definition for it can
 *      be resolved (registry overlay or bundled pack), it wins.
 *   2. Otherwise — unset, or a dangling pointer at a calendar nothing
 *      defines — fall back to {@link FALLBACK_CALENDAR_ID} deterministically.
 *
 * Returning the id (not the `Calendar`) keeps the cursor's
 * calendar-keyed persistence + reset logic working on a stable handle;
 * {@link loadActiveCalendar} turns the id into the runtime calendar.
 */
export function resolveActiveCalendarId(
  config: ConfigState,
  registry: CalendarRegistry,
): string {
  const chosen = config.canonical_calendar;
  if (chosen !== undefined && chosen.length > 0 && isResolvable(chosen, registry)) {
    return chosen;
  }
  return FALLBACK_CALENDAR_ID;
}

/**
 * Resolve a calendar id to the runtime `Calendar` the conversion engine
 * consumes. A registry `CalendarDefinition` (a `pack.define_calendar`
 * folded into the overlay) takes precedence over a bundled-pack calendar
 * of the same id; the definition envelope is re-validated through
 * `@dl/calendar`'s `CalendarDefinitionSchema` (the `@dl/shared` fold only
 * checks the opaque-id + name envelope) before `asCalendar` projects it.
 *
 * Throws only if the id resolves to neither a valid registry definition
 * nor a bundled calendar — callers that pass an id from
 * {@link resolveActiveCalendarId} never hit this, since that function
 * already guarantees resolvability (falling back otherwise).
 */
export function loadActiveCalendar(
  calendarId: string,
  registry: CalendarRegistry,
): Calendar {
  const def = registry.find((c) => c.id === calendarId);
  if (def !== undefined) {
    return asCalendar(CalendarDefinitionSchema.parse(def));
  }
  return loadBundledCalendar(calendarId);
}

/**
 * Is the calendar definition for `calendarId` a **plugin-mode** calendar
 * (#242, slice C1)? A plugin calendar LOADS — its pack's entities / traits /
 * types stay available — but the v1 engine cannot decompose it into cycles,
 * so calendar-dependent surfaces (date picker, formatting) must DEGRADE with
 * a "calendar not fully supported" notice instead of calling `asCalendar`
 * (which throws for a plugin calendar).
 *
 * Reads the raw registry definition (a folded `pack.define_calendar`) and
 * keys on `isPluginCalendar` BEFORE any structural parse. Bundled calendars
 * are always structured, so an id not in the registry is never a plugin.
 */
export function isPluginCalendarId(calendarId: string, registry: CalendarRegistry): boolean {
  const def = registry.find((c) => c.id === calendarId);
  return isPluginCalendar(def);
}

/**
 * The outcome of resolving the active calendar to a runtime `Calendar`
 * (#242). Distinguishes a structured calendar (renders normally) from a
 * plugin calendar (degrades) so the host can show the notice WITHOUT a
 * try/catch around `asCalendar`:
 *
 *   - `{ kind: 'structured', calendar }` — render normally.
 *   - `{ kind: 'plugin' }` — a plugin-mode calendar; the host renders the
 *     "calendar not fully supported" notice. NO plugin code runs.
 *   - `{ kind: 'unresolvable' }` — neither a registry nor bundled definition
 *     resolves (a dangling pointer that slipped past `resolveActiveCalendarId`);
 *     treated as a degrade, not a crash.
 */
export type ActiveCalendarResolution =
  | { readonly kind: 'structured'; readonly calendar: Calendar }
  | { readonly kind: 'plugin' }
  | { readonly kind: 'unresolvable' };

/**
 * Resolve a calendar id to a {@link ActiveCalendarResolution} — the safe,
 * total counterpart of {@link loadActiveCalendar} that never throws (#242).
 * A plugin-mode calendar yields `{ kind: 'plugin' }` (degrade); a structured
 * calendar yields its runtime `Calendar`. Use this at any calendar-dependent
 * surface that must stay rendered in the presence of a plugin calendar.
 */
export function resolveActiveCalendar(
  calendarId: string,
  registry: CalendarRegistry,
): ActiveCalendarResolution {
  const def = registry.find((c) => c.id === calendarId);
  if (def !== undefined) {
    if (isPluginCalendar(def)) return { kind: 'plugin' };
    try {
      return { kind: 'structured', calendar: asCalendar(CalendarDefinitionSchema.parse(def)) };
    } catch {
      return { kind: 'unresolvable' };
    }
  }
  try {
    return { kind: 'structured', calendar: loadBundledCalendar(calendarId) };
  } catch {
    return { kind: 'unresolvable' };
  }
}
